/**
 * EnergyChart — SVG line chart with gradient fill for daily energy generation.
 *
 * Features:
 * - Smooth cubic bezier curve
 * - Gradient fill under curve
 * - Dotted horizontal grid lines only (no border box)
 * - Hover: vertical indicator + tooltip
 * - Mock bell-curve data (peak ~noon)
 */

import { useState, useRef, useCallback } from 'react';
import './EnergyChart.css';

interface DataPoint {
  hour: number;   // 6..18
  kw: number;
}

// Bell-curve shaped mock data
const MOCK_DATA: DataPoint[] = [
  { hour: 6,  kw: 2.1 },
  { hour: 7,  kw: 8.4 },
  { hour: 8,  kw: 18.7 },
  { hour: 9,  kw: 29.3 },
  { hour: 10, kw: 38.6 },
  { hour: 11, kw: 44.9 },
  { hour: 12, kw: 47.8 },
  { hour: 13, kw: 46.2 },
  { hour: 14, kw: 41.5 },
  { hour: 15, kw: 33.8 },
  { hour: 16, kw: 22.4 },
  { hour: 17, kw: 11.6 },
  { hour: 18, kw: 3.2 },
];

const CHART_PAD = { top: 16, right: 16, bottom: 36, left: 40 };

function formatHour(h: number) {
  if (h === 12) return '12pm';
  if (h > 12)  return `${h - 12}pm`;
  return `${h}am`;
}

function buildSmoothPath(points: { px: number; py: number }[], close = false): string {
  if (points.length < 2) return '';
  let d = `M ${points[0].px} ${points[0].py}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx = (prev.px + curr.px) / 2;
    d += ` C ${cpx} ${prev.py} ${cpx} ${curr.py} ${curr.px} ${curr.py}`;
  }
  if (close) {
    const last = points[points.length - 1];
    const first = points[0];
    d += ` L ${last.px} ${first.py + 200} L ${first.px} ${first.py + 200} Z`;
  }
  return d;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  hour: number;
  kw: number;
}

interface EnergyChartProps {
  data?: DataPoint[];
  title?: string;
}

export default function EnergyChart({ data = MOCK_DATA, title = "Today's Generation" }: EnergyChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, hour: 0, kw: 0 });
  const [hoverX, setHoverX] = useState<number | null>(null);

  const totalKwh = data.reduce((s, d) => s + d.kw, 0).toFixed(1);

  // We don't know the rendered size until mount, so use a fixed viewBox
  const VW = 560;
  const VH = 200;
  const innerW = VW - CHART_PAD.left - CHART_PAD.right;
  const innerH = VH - CHART_PAD.top  - CHART_PAD.bottom;

  const minHour = data[0].hour;
  const maxHour = data[data.length - 1].hour;
  const maxKw   = Math.max(...data.map((d) => d.kw));

  const toPixel = useCallback((hour: number, kw: number) => ({
    px: CHART_PAD.left + ((hour - minHour) / (maxHour - minHour)) * innerW,
    py: CHART_PAD.top  + (1 - kw / (maxKw * 1.1)) * innerH,
  }), [innerW, innerH, minHour, maxHour, maxKw]);

  const points = data.map((d) => toPixel(d.hour, d.kw));

  const linePath  = buildSmoothPath(points);
  const fillPath  = buildSmoothPath(points, false)
    + ` L ${points[points.length - 1].px} ${CHART_PAD.top + innerH}`
    + ` L ${points[0].px} ${CHART_PAD.top + innerH} Z`;

  // Y grid lines
  const gridKws = [0, maxKw * 0.25, maxKw * 0.5, maxKw * 0.75, maxKw].map((v) => Math.round(v));

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VW;
    const relX = svgX - CHART_PAD.left;
    const hourRaw = minHour + (relX / innerW) * (maxHour - minHour);
    const hourClamped = Math.max(minHour, Math.min(maxHour, hourRaw));

    // Find nearest data point
    let nearest = data[0];
    let nearestDist = Infinity;
    for (const d of data) {
      const dist = Math.abs(d.hour - hourClamped);
      if (dist < nearestDist) { nearestDist = dist; nearest = d; }
    }

    const { px, py } = toPixel(nearest.hour, nearest.kw);
    setHoverX(px);
    setTooltip({ visible: true, x: px, y: py, hour: nearest.hour, kw: nearest.kw });
  };

  const handleMouseLeave = () => {
    setHoverX(null);
    setTooltip((t) => ({ ...t, visible: false }));
  };

  return (
    <div className="echart-card card">
      {/* Header */}
      <div className="echart-header">
        <span className="echart-title">{title}</span>
        <div className="echart-header__right">
          <span className="echart-total tabular">{totalKwh} kWh</span>
          <span className="echart-legend-dot" />
          <span className="echart-legend-label">Generation</span>
        </div>
      </div>

      {/* SVG chart */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        width="100%"
        height={VH}
        className="echart-svg"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        aria-label="Daily energy generation chart"
      >
        <defs>
          <linearGradient id="chart-fill-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#2F80FF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#2F80FF" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Dotted horizontal grid lines */}
        {gridKws.map((kw) => {
          const { py } = toPixel(minHour, kw);
          return (
            <g key={kw}>
              <line
                x1={CHART_PAD.left} y1={py}
                x2={CHART_PAD.left + innerW} y2={py}
                stroke="var(--gray-200)"
                strokeWidth="1"
                strokeDasharray="3 4"
              />
              <text
                x={CHART_PAD.left - 6} y={py}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="10"
                fill="var(--text-muted)"
                fontFamily="var(--font-main)"
              >
                {kw}
              </text>
            </g>
          );
        })}

        {/* Gradient fill */}
        <path d={fillPath} fill="url(#chart-fill-gradient)" />

        {/* Line */}
        <path d={linePath} fill="none" stroke="#2F80FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* X axis labels */}
        {data.filter((_, i) => i % 2 === 0).map((d) => {
          const { px } = toPixel(d.hour, 0);
          return (
            <text
              key={d.hour}
              x={px}
              y={CHART_PAD.top + innerH + 18}
              textAnchor="middle"
              fontSize="10"
              fill="var(--text-muted)"
              fontFamily="var(--font-main)"
            >
              {formatHour(d.hour)}
            </text>
          );
        })}

        {/* Hover indicator */}
        {hoverX !== null && (
          <line
            x1={hoverX} y1={CHART_PAD.top}
            x2={hoverX} y2={CHART_PAD.top + innerH}
            stroke="var(--primary-blue)"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.5"
          />
        )}

        {/* Hover dot */}
        {tooltip.visible && (
          <circle cx={tooltip.x} cy={tooltip.y} r="4" fill="#2F80FF" stroke="white" strokeWidth="2" />
        )}

        {/* Tooltip */}
        {tooltip.visible && (() => {
          const tx = Math.min(tooltip.x + 8, VW - 80);
          const ty = Math.max(tooltip.y - 32, 4);
          return (
            <g>
              <rect x={tx} y={ty} width={72} height={24} rx="6" fill="var(--text-main)" opacity="0.88" />
              <text x={tx + 36} y={ty + 12} textAnchor="middle" dominantBaseline="middle"
                fontSize="11" fontWeight="600" fill="#fff" fontFamily="var(--font-main)">
                {tooltip.kw.toFixed(1)} kW
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
